import React from "react";
import { TrendingUp, TrendingDown, Minus, RefreshCw } from "lucide-react";
import { Sparkline } from "./Sparkline";
import { Skeleton } from "./ui/skeleton";
import { CollapseToggle } from "./CollapseToggle";
import { useCollapsed } from "../hooks/useCollapsed";

const SentimentBadge = ({ sentiment }) => {
  const map = {
    BULLISH: { color: "var(--bull)", bg: "var(--bull-muted)", icon: TrendingUp },
    BEARISH: { color: "var(--bear)", bg: "var(--bear-muted)", icon: TrendingDown },
    NEUTRAL: { color: "var(--text-secondary)", bg: "rgba(255,255,255,0.06)", icon: Minus },
  };
  const cfg = map[sentiment] || map.NEUTRAL;
  const Icon = cfg.icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-mono-data border"
      style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.color }}
    >
      <Icon size={11} />
      {sentiment}
    </span>
  );
};

const IndexCard = ({ idx }) => {
  const positive = idx.change >= 0;
  const color = positive ? "var(--bull)" : "var(--bear)";
  return (
    <div
      data-testid={`sentiment-card-${idx.name.replace(/\s+/g, "-").toLowerCase()}`}
      className="border-grid surface p-5 flex flex-col gap-3 hover:bg-white/[0.02] transition-colors"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] tracking-[0.2em] text-muted-3 font-mono-data uppercase flex items-center gap-1.5">
            {idx.symbol}
            <span
              data-testid={`sentiment-source-${idx.name.replace(/\s+/g, "-").toLowerCase()}`}
              title={idx.source === "upstox"
                ? "Live LTP from Upstox"
                : "Yahoo Finance — can lag or get stuck for Indian indices. Reconnect Upstox for live prices."}
              style={{ color: idx.source === "upstox" ? "var(--bull)" : "var(--warn, #FFB66E)" }}
            >
              · {idx.source === "upstox" ? "LIVE" : "DELAYED"}
            </span>
          </div>
          <h3 className="font-heading text-base md:text-lg font-semibold mt-0.5">
            {idx.name}
          </h3>
        </div>
        <SentimentBadge sentiment={idx.sentiment} />
      </div>

      <div className="flex items-end justify-between">
        <div>
          <div
            data-testid={`sentiment-price-${idx.name.replace(/\s+/g, "-").toLowerCase()}`}
            className="font-mono-data text-2xl md:text-3xl font-semibold tracking-tight"
          >
            {idx.price.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
          </div>
          <div
            className="font-mono-data text-sm mt-0.5"
            style={{ color }}
          >
            {positive ? "+" : ""}
            {idx.change.toFixed(2)} ({positive ? "+" : ""}
            {idx.change_pct.toFixed(2)}%)
          </div>
        </div>
        <Sparkline data={idx.sparkline} positive={positive} />
      </div>

      <div className="grid grid-cols-2 gap-2 pt-3 border-t border-grid">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
            Day High
          </div>
          <div className="font-mono-data text-sm">{idx.day_high?.toFixed(2) || "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
            Day Low
          </div>
          <div className="font-mono-data text-sm">{idx.day_low?.toFixed(2) || "—"}</div>
        </div>
      </div>
    </div>
  );
};

export const MarketSentiment = ({ data, loading, onRefresh, refreshing }) => {
  const [collapsed, toggle] = useCollapsed("market-sentiment");
  return (
    <section data-testid="market-sentiment-section" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-[0.25em] text-muted-3 font-mono-data uppercase">
            01 / Market Pulse
          </div>
          <h2 className="font-heading text-xl md:text-2xl font-bold mt-1">
            Market Sentiment
          </h2>
        </div>
        <div className="flex items-center gap-3">
          {data && (
            <span
              data-testid="market-sentiment-upstox-status"
              className="text-[10px] uppercase tracking-wider font-mono-data hidden sm:inline"
              style={{ color: data.upstox_connected ? "var(--bull)" : "var(--warn, #FFB66E)" }}
              title={data.upstox_connected
                ? "Upstox connected — prices overlaid with live LTPs where available"
                : "Upstox not connected — showing Yahoo Finance data only, which can lag or get stuck. Reconnect Upstox from the header."}
            >
              {data.upstox_connected ? "Upstox Live" : "Upstox Disconnected"}
            </span>
          )}
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={refreshing}
              data-testid="market-sentiment-refresh-btn"
              title="Re-fetch live prices (bypasses cache)"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-grid text-[10px] uppercase tracking-[0.2em] font-mono-data hover:border-white/60 transition-colors disabled:opacity-40"
            >
              <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          )}
          {data?.overall && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
                Overall
              </div>
              <SentimentBadge sentiment={data.overall.sentiment} />
            </div>
          )}
          <CollapseToggle
            collapsed={collapsed}
            onToggle={toggle}
            testId="market-sentiment-toggle"
          />
        </div>
      </div>

      {!collapsed && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {loading || !data
            ? Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="border-grid surface p-5 space-y-3">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-8 w-32" />
                  <Skeleton className="h-9 w-full" />
                </div>
              ))
            : data.indices.map((idx) => <IndexCard key={idx.symbol} idx={idx} />)}
        </div>
      )}
    </section>
  );
};